import { useState } from 'react';
import { useAtom } from 'jotai';
import { 
  authCredentialsAtom, 
  sprintsAtom, 
  issuesAtom, 
  projectsAtom, 
  userAllocationsAtom, 
  isLoadingAtom, 
  errorAtom 
} from '@/lib/store';
import { ApiService } from '@/services/api';
import { calculateUserAllocations } from '@/lib/planning';

export function useSprintData() {
  const [authCredentials] = useAtom(authCredentialsAtom);
  const [sprints, setSprints] = useAtom(sprintsAtom);
  const [, setIssues] = useAtom(issuesAtom);
  const [, setProjects] = useAtom(projectsAtom);
  const [userAllocations, setUserAllocations] = useAtom(userAllocationsAtom);
  const [isLoading, setIsLoading] = useAtom(isLoadingAtom);
  const [error, setError] = useAtom(errorAtom);
  const [updateMessage, setUpdateMessage] = useState('');

  const fetchData = async () => {
    if (!authCredentials) return;
    
    setIsLoading(true);
    setUpdateMessage('');
    setError(null);
    
    try {
      const storyPointField = authCredentials.storyPointField || 'customfield_10022';
      const data = await ApiService.fetchSprintData(authCredentials, storyPointField);
      
      const { sprints, issues, projects } = data;
      
      // Calculate user allocations
      const userAllocations = calculateUserAllocations(issues, sprints);
      
      // Update state
      setSprints(sprints);
      setIssues(issues);
      setProjects(projects || []);
      setUserAllocations(userAllocations);
      
      setUpdateMessage('Data updated successfully');
      
      // Clear message after 3 seconds
      setTimeout(() => setUpdateMessage(''), 3000);
      
      return { sprints, issues, projects, userAllocations };
    } catch (err: unknown) {
      const error = err as Error;
      console.error('Data fetch error:', error);
      setError(error.message || 'Failed to fetch data');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    sprints,
    userAllocations,
    isLoading,
    error,
    updateMessage,
    fetchData
  };
}